// src/permissions/checkPermission.js
// Vérification des permissions admin - NE PAS MODIFIER

import { ADMIN_ROLES, PERMISSIONS, ROLE_LEVEL } from '../constants/roles';

/**
 * Vérifier si un admin a une permission spécifique
 * @param {string} role - Rôle admin (SUPER_ADMIN, ADMIN, EDITOR, SUPPORT)
 * @param {string} permission - Permission à vérifier
 * @returns {boolean} true si l'admin a la permission
 */
export const hasPermission = (role, permission) => {
  if (!role || !permission) return false;

  const adminPermissions = PERMISSIONS[role] || [];
  return adminPermissions.includes(permission);
};

/**
 * Vérifier si un admin peut accéder à une page/section
 * @param {string} role - Rôle admin
 * @param {string} resource - Ressource (ex: 'listings', 'reservations', 'admins')
 * @returns {boolean} true si accès autorisé
 */
export const canAccessResource = (role, resource) => {
  const resourcePermissions = {
    listings: 'manage_listings',
    reservations: 'manage_reservations',
    owners: 'manage_owners',
    users: 'manage_users',
    admins: 'manage_admins',
    quartiers: 'manage_quartiers',
    types: 'manage_types',
    reviews: 'manage_reviews',
    reports: 'manage_reports',
    stats: 'view_stats',
    auditLogs: 'view_audit_logs',
  };

  const requiredPermission = resourcePermissions[resource];
  return requiredPermission ? hasPermission(role, requiredPermission) : false;
};

/**
 * Vérifier si un admin peut effectuer une action spécifique
 * @param {string} role - Rôle admin
 * @param {string} action - Action (create, read, update, delete)
 * @param {string} resource - Ressource
 * @returns {boolean} true si action autorisée
 */
export const canPerformAction = (role, action, resource) => {
  const actionPermissions = {
    create: {
      listings: 'manage_listings',
      admins: 'manage_admins',
      quartiers: 'manage_quartiers',
      types: 'manage_types',
    },
    read: {
      listings: 'manage_listings',
      reservations: 'manage_reservations',
      users: 'manage_users',
      admins: 'manage_admins',
      stats: 'view_stats',
      auditLogs: 'view_audit_logs',
    },
    update: {
      listings: 'manage_listings',
      admins: 'manage_admins',
      quartiers: 'manage_quartiers',
      types: 'manage_types',
    },
    delete: {
      listings: 'delete_listings',
      admins: 'manage_admins',
      users: 'manage_users',
      quartiers: 'manage_quartiers',
      types: 'manage_types',
    },
    validate: {
      listings: 'validate_listings',
      reviews: 'manage_reviews',
    },
  };

  const requiredPermission = actionPermissions[action]?.[resource];
  return requiredPermission ? hasPermission(role, requiredPermission) : false;
};

/**
 * Obtenir le niveau de rôle (pour comparer la hiérarchie)
 * @param {string} role - Rôle admin
 * @returns {number} Niveau (4 = SUPER_ADMIN, 3 = ADMIN, etc.)
 */
export const getRoleLevel = (role) => {
  return ROLE_LEVEL[role] || 0;
};

/**
 * Vérifier si un rôle est hiérarchiquement supérieur à un autre
 * @param {string} role1 - Premier rôle
 * @param {string} role2 - Deuxième rôle
 * @returns {boolean} true si role1 > role2
 */
export const isRoleSuperior = (role1, role2) => {
  return getRoleLevel(role1) > getRoleLevel(role2);
};

/**
 * Vérifier si un admin peut modifier un autre admin
 * @param {string} modifierRole - Rôle de l'admin qui modifie
 * @param {string} targetRole - Rôle de l'admin à modifier
 * @returns {boolean} true si modification autorisée
 */
export const canModifyAdmin = (modifierRole, targetRole) => {
  // Seul SUPER_ADMIN peut modifier les rôles
  if (modifierRole !== ADMIN_ROLES.SUPER_ADMIN) {
    return false;
  }

  // SUPER_ADMIN ne peut pas modifier un autre SUPER_ADMIN
  if (targetRole === ADMIN_ROLES.SUPER_ADMIN) {
    return false;
  }

  return true;
};

/**
 * Vérifier si un admin peut supprimer un autre admin
 * @param {string} deleterRole - Rôle de l'admin qui supprime
 * @param {string} targetRole - Rôle de l'admin à supprimer
 * @returns {boolean} true si suppression autorisée
 */
export const canDeleteAdmin = (deleterRole, targetRole) => {
  // Seul SUPER_ADMIN peut supprimer
  if (deleterRole !== ADMIN_ROLES.SUPER_ADMIN) {
    return false;
  }

  // SUPER_ADMIN ne peut pas se supprimer soi-même
  if (targetRole === ADMIN_ROLES.SUPER_ADMIN) {
    return false;
  }

  return true;
};

/**
 * Vérifier si un admin peut valider/rejeter une annonce
 * @param {string} role - Rôle admin
 * @returns {boolean} true si validation autorisée
 */
export const canValidateListing = (role) => {
  return hasPermission(role, 'validate_listings');
};

/**
 * Vérifier si un admin peut supprimer une annonce
 * @param {string} role - Rôle admin
 * @returns {boolean} true si suppression autorisée
 */
export const canDeleteListing = (role) => {
  return hasPermission(role, 'delete_listings');
};

/**
 * Vérifier si un admin peut gérer les utilisateurs
 * @param {string} role - Rôle admin
 * @returns {boolean} true si gestion autorisée
 */
export const canManageUsers = (role) => {
  return hasPermission(role, 'manage_users');
};

/**
 * Vérifier si un admin peut voir les logs d'audit
 * @param {string} role - Rôle admin
 * @returns {boolean} true si accès autorisé
 */
export const canViewAuditLogs = (role) => {
  return hasPermission(role, 'view_audit_logs');
};

/**
 * Obtenir la liste des permissions pour un rôle
 * @param {string} role - Rôle admin
 * @returns {Array} Tableau de permissions
 */
export const getPermissionsForRole = (role) => {
  return PERMISSIONS[role] || [];
};

/**
 * Obtenir la description d'une permission
 * @param {string} permission - Permission
 * @param {string} language - 'fr' ou 'en'
 * @returns {string} Description
 */
export const getPermissionDescription = (permission, language = 'fr') => {
  const descriptions = {
    fr: {
      manage_admins: 'Gérer les administrateurs',
      view_audit_logs: 'Voir les logs d\'audit',
      manage_listings: 'Gérer les annonces',
      validate_listings: 'Valider/Rejeter les annonces',
      delete_listings: 'Supprimer les annonces',
      manage_reservations: 'Gérer les réservations',
      manage_owners: 'Gérer les propriétaires',
      manage_quartiers: 'Gérer les quartiers',
      manage_types: 'Gérer les types d\'annonces',
      manage_users: 'Gérer les utilisateurs',
      manage_reviews: 'Gérer les avis',
      manage_reports: 'Gérer les signalements',
      view_stats: 'Voir les statistiques',
      manage_payments: 'Gérer les paiements',
      contact_clients: 'Contacter les clients',
      all: 'Toutes les permissions',
    },
    en: {
      manage_admins: 'Manage administrators',
      view_audit_logs: 'View audit logs',
      manage_listings: 'Manage listings',
      validate_listings: 'Validate/Reject listings',
      delete_listings: 'Delete listings',
      manage_reservations: 'Manage reservations',
      manage_owners: 'Manage owners',
      manage_quartiers: 'Manage neighborhoods',
      manage_types: 'Manage listing types',
      manage_users: 'Manage users',
      manage_reviews: 'Manage reviews',
      manage_reports: 'Manage reports',
      view_stats: 'View statistics',
      manage_payments: 'Manage payments',
      contact_clients: 'Contact clients',
      all: 'All permissions',
    },
  };

  return descriptions[language]?.[permission] || descriptions.fr[permission] || permission;
};

/**
 * Vérifier si un rôle est valide
 * @param {string} role - Rôle à vérifier
 * @returns {boolean} true si valide
 */
export const isValidRole = (role) => {
  return Object.values(ADMIN_ROLES).includes(role);
};

/**
 * Protéger une route admin
 * @param {string} role - Rôle de l'utilisateur
 * @param {string} requiredPermission - Permission requise
 * @returns {boolean} true si accès autorisé
 */
export const protectRoute = (role, requiredPermission) => {
  if (!role) return false;
  if (!isValidRole(role)) return false;
  return hasPermission(role, requiredPermission);
};

/**
 * Obtenir un message d'accès refusé personnalisé
 * @param {string} role - Rôle de l'utilisateur
 * @param {string} resource - Ressource accédée
 * @param {string} language - 'fr' ou 'en'
 * @returns {string} Message d'erreur
 */
export const getDeniedAccessMessage = (role, resource, language = 'fr') => {
  const messages = {
    fr: {
      noRole: 'Authentification requise',
      noPermission: `Vous n'avez pas accès à ${resource}`,
      insufficientLevel: 'Niveau de permission insuffisant',
      invalidAction: 'Action non autorisée',
    },
    en: {
      noRole: 'Authentication required',
      noPermission: `You don't have access to ${resource}`,
      insufficientLevel: 'Insufficient permission level',
      invalidAction: 'Action not allowed',
    },
  };

  if (!role) return messages[language]?.noRole || messages.fr.noRole;
  return messages[language]?.noPermission || messages.fr.noPermission;
};

/**
 * Générer un badge de rôle avec couleur
 * @param {string} role - Rôle
 * @returns {object} { label, color, bgColor }
 */
export const getRoleBadge = (role) => {
  const badges = {
    SUPER_ADMIN: {
      label: 'Super Admin',
      color: '#FFD700',
      bgColor: '#1C2537',
    },
    ADMIN: {
      label: 'Admin',
      color: '#48BB78',
      bgColor: '#1C2537',
    },
    EDITOR: {
      label: 'Éditeur',
      color: '#4299E1',
      bgColor: '#1C2537',
    },
    SUPPORT: {
      label: 'Support',
      color: '#ED8936',
      bgColor: '#1C2537',
    },
  };

  return badges[role] || badges.SUPPORT;
};

/**
 * Calculer le score de permission (combien de permissions a le rôle)
 * @param {string} role - Rôle
 * @returns {number} Nombre de permissions
 */
export const getPermissionScore = (role) => {
  const permissions = getPermissionsForRole(role);
  return permissions.length;
};

/**
 * Obtenir les rôles que le rôle courant peut assigner
 * @param {string} role - Rôle de l'admin
 * @returns {Array} Tableau de rôles assignables
 */
export const getAssignableRoles = (role) => {
  const assignable = {
    SUPER_ADMIN: [
      ADMIN_ROLES.SUPER_ADMIN,
      ADMIN_ROLES.ADMIN,
      ADMIN_ROLES.EDITOR,
      ADMIN_ROLES.SUPPORT,
    ],
    ADMIN: [ADMIN_ROLES.EDITOR, ADMIN_ROLES.SUPPORT],
    EDITOR: [],
    SUPPORT: [],
  };

  return assignable[role] || [];
};